import type { GridColDef } from '@mui/x-data-grid';
import type { PerUnitProduct } from '@vy/services';
import type { Currency, FixedProduct, UsageProduct } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined, isNumber, withDefault } from '@vy/utils';
import { FC } from 'react';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { getBillingIntervalLabel } from '../../ContractSummaryDetails/contract-summary-details-utils/get-billing-interval-label';
import { ContractTable } from '../../shared/ContractTable';
import { AnrokCell } from '../../shared/AnrokCell';
import type { OnEditAnrokHandler } from '../../shared/anrok-cell-types';
import { ErpCell } from '../../shared/ErpCell';
import { isProduction } from '../../../../../utils/env-indicator';
import type { ContractFixedFeesColumns } from '../contract-fixed-fees-types/contract-fixed-fees-columns';
import { CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS } from '../contract-fixed-fees-const/contract-fixed-fees-columns-keys';
import { getErpName } from '../contract-fixed-fees-utils/get-contract-fixed-fees-columns-keys';
import {
  formatPaymentLabel,
  ONE_TIME_FEE_LABEL,
} from '../contract-fixed-fees-utils/get-contract-fixed-fees-rows';

type ContractFixedFeesTableProps = {
  rows: ContractFixedFeesColumns[];
  columns: GridColDef<ContractFixedFeesColumns>[];
  currency: Currency;
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void;
  onEditAnrok?: OnEditAnrokHandler;
};

export const ContractFixedFeesTable: FC<ContractFixedFeesTableProps> = ({
  rows,
  columns,
  currency,
  onEditErp,
  onEditAnrok,
}) => {
  return (
    <ContractTable
      rows={rows}
      columns={columns}
      currency={currency}
      renderCell={(row, column) => renderCellContent(row, column, currency, onEditErp, onEditAnrok)}
      hasExpandableRows={false}
    />
  );
};

function renderCellContent(
  row: ContractFixedFeesColumns,
  column: GridColDef<ContractFixedFeesColumns>,
  currency: Currency,
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void,
  onEditAnrok?: OnEditAnrokHandler,
): React.ReactNode {
  switch (column.field) {
    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.AMOUNT:
      return isNumber(row.amount) ? (
        <VyTableCell value={<Price currency={currency}>{row.amount}</Price>} containerClassName="justify-start" />
      ) : (
        withDefault(row.amount, '--')
      );

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRICE_PER_UNIT:
      return isNotNullOrUndefined(row.pricePerUnit) ? (
        <VyTableCell
          value={<Price currency={currency}>{row.pricePerUnit}</Price>}
          containerClassName="justify-start"
        />
      ) : (
        '--'
      );

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.BILLING_INTERVAL: {
      if (row.pricingType === ONE_TIME_FEE_LABEL) {
        return <VyTableCell value="--" />;
      }
      const billingIntervalValue = isNotNullOrUndefined(row.billingInterval)
        ? getBillingIntervalLabel(row.billingInterval)
        : '--';
      return (
        <VyTableCell
          value={billingIntervalValue}
          containerClassName={billingIntervalValue !== '--' ? 'justify-start' : undefined}
        />
      );
    }

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PAYMENT_TERM: {
      const paymentTermValue = isNotNullOrUndefined(row.paymentTerm) ? formatPaymentLabel(row.paymentTerm) : '--';
      return (
        <VyTableCell
          value={paymentTermValue}
          containerClassName={paymentTermValue !== '--' ? 'justify-start' : undefined}
        />
      );
    }

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.ERP: {
      return <ErpCell value={getErpName(row.erp)} product={row.product} productName={row.productName} onEditErp={onEditErp} />;
    }

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.MARKETPLACE_PRODUCT_ID:
      return <VyTableCell value={row.marketplaceProductId} />;

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.MARKETPLACE_DIMENSION:
      return <VyTableCell value={row.marketplaceDimension} />;

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.ANROK_ID: {
      const isInherited = isNullOrUndefined(row.anrokOverrideId) && isNotNullOrUndefined(row.anrokCatalogDefaultId);
      return (
        <AnrokCell
          value={row.anrokId}
          product={row.product}
          productName={row.productName}
          isInherited={isInherited}
          catalogAnrokId={row.anrokCatalogDefaultId}
          overrideAnrokId={row.anrokOverrideId}
          onEditAnrok={onEditAnrok}
        />
      );
    }

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRODUCT_NAME:
      return <VyTableCell value={row.productName} containerClassName="justify-start" />;

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRICING_TYPE:
      return <VyTableCell value={row.pricingType} containerClassName="justify-start" />;

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.DISCOUNT:
      return <VyTableCell value={row.discount} containerClassName="justify-start" />;

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.QUANTITY: {
      const quantityValue = row.quantity?.toString();
      return (
        <VyTableCell
          value={quantityValue}
          containerClassName={quantityValue ? 'justify-start' : undefined}
        />
      );
    }

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.SCHEDULE_START_DATE:
      return (
        <VyTableCell
          value={row.scheduleStartDate}
          containerClassName={row.scheduleStartDate ? 'justify-start' : undefined}
        />
      );

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.SCHEDULE_END_DATE:
      return (
        <VyTableCell
          value={row.scheduleEndDate}
          containerClassName={row.scheduleEndDate ? 'justify-start' : undefined}
        />
      );

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.INSTALLMENTS: {
      const installmentsValue = row.shouldShowInstallments ? row.installments?.toString() : undefined;
      return (
        <VyTableCell
          value={installmentsValue}
          containerClassName={installmentsValue ? 'justify-start' : undefined}
        />
      );
    }

    case CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRO_RATE: {
      const proRateValue = row.proRate?.toString();
      return (
        <VyTableCell
          value={proRateValue}
          containerClassName={proRateValue ? 'justify-start' : undefined}
        />
      );
    }

    default:
      if (!isProduction()) {
        console.warn(`[ContractFixedFeesTable] No cell mapping for column field: "${column.field}". Add a case to renderCellContent.`);
      }
      return '--';
  }
}


